import React from 'react';
import { useLocation } from 'react-router-dom';
import { HelpCircle } from 'lucide-react';

/**
 * Page footer.
 *
 * Renders:
 *   © {year} Ergoniq Pvt Ltd          {company} · {env-chip if not prod} · v{version} · Support
 *
 * Reads:
 *   - user.company_name (prop)
 *   - process.env.REACT_APP_ENV       ("production" | "qa" | "dev" | "staging" | ...)
 *                                     falls back to process.env.NODE_ENV
 *   - process.env.REACT_APP_VERSION   baked at build time; falls back to "dev"
 *
 * Usage:
 *   <Footer user={user} />
 *
 * Apply per-page (matches TabNavigation pattern): import + place at the bottom
 * of the hub's main wrapper, after all page content.
 */

const APP_YEAR = new Date().getFullYear();
const COMPANY = 'SAARV Solutions Pvt Ltd';
const VERSION = process.env.REACT_APP_VERSION || 'dev';
const RAW_ENV = (process.env.REACT_APP_ENV || process.env.NODE_ENV || 'development').toLowerCase();

const ENV_DISPLAY = {
  production:  null,            // hide chip in prod (no clutter)
  prod:        null,
  qa:          { label: 'QA',      tone: 'amber' },
  staging:     { label: 'Staging', tone: 'amber' },
  uat:         { label: 'UAT',     tone: 'amber' },
  development: { label: 'Dev',     tone: 'gray' },
  dev:         { label: 'Dev',     tone: 'gray' },
};

const TONE_CLASSES = {
  amber: 'bg-amber-100 text-amber-800 border-amber-200',
  gray:  'bg-gray-100  text-gray-700  border-gray-200',
};

const Footer = ({ user }) => {
  const location = useLocation();
  // Hide the Support link when the user is already on the help portal —
  // otherwise clicking it just opens /help in a new tab repeatedly.
  const isOnHelpPage = location.pathname === '/help' || location.pathname.startsWith('/help/');

  const envCfg = ENV_DISPLAY[RAW_ENV];
  const envChip = envCfg
    ? <span className={`px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider border rounded ${TONE_CLASSES[envCfg.tone]}`}>
        {envCfg.label}
      </span>
    : null;

  return (
    <footer className="border-t border-gray-200 bg-white mt-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-xs text-gray-500">
        {/* Left: legal */}
        <div>
          © {APP_YEAR} {COMPANY}
        </div>

        {/* Right: tenant · env · version · support */}
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          {envChip && (
            <>
              {envChip}
              <span className="text-gray-300">·</span>
            </>
          )}
          <span className="font-mono">v{VERSION}</span>
          {!isOnHelpPage && (
            <>
              <span className="text-gray-300">·</span>
              <button
                type="button"
                onClick={() => window.open('/help', '_blank', 'noopener,noreferrer')}
                className="inline-flex items-center gap-1 text-blue-700 hover:text-blue-900 hover:underline"
                title="Open the help portal"
              >
                <HelpCircle className="w-3.5 h-3.5" />
                Support
              </button>
            </>
          )}
        </div>
      </div>
    </footer>
  );
};

export default Footer;
